/**
 * WhyEqualParentingSection Component
 *
 * Displays key points explaining the importance of equal shared parenting
 * with statistics and research sources.
 *
 * Contract: specs/001-home-page-sections/contracts/WhyEqualParentingSection.ts
 */

import React from 'react'
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Link,
} from '@mui/material'
import {
  FamilyRestroom,
  Favorite,
  TrendingUp,
  Gavel,
} from '@mui/icons-material'
import { WhyEqualParentingContent } from '@/data/types'

export interface WhyEqualParentingSectionProps {
  /**
   * Content for the section (title, key points, statistics)
   */
  content: WhyEqualParentingContent

  /**
   * Callback when user clicks statistic source link
   * @param sourceUrl - The URL of the research source
   */
  onSourceClick?: (sourceUrl: string) => void

  /**
   * Optional CSS class name for styling
   */
  className?: string

  /**
   * Test ID for automated testing
   */
  testId?: string
}

/**
 * Map icon strings to MUI icon components
 */
const ICON_MAP: Record<string, React.ElementType> = {
  FamilyRestroom,
  Favorite,
  TrendingUp,
  Gavel,
}

/**
 * WhyEqualParentingSection Component
 */
export const WhyEqualParentingSection: React.FC<
  WhyEqualParentingSectionProps
> = ({
  content,
  onSourceClick,
  className,
  testId = 'why-equal-parenting-section',
}) => {
  const handleSourceClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    sourceUrl: string
  ) => {
    if (onSourceClick) {
      event.preventDefault()
      onSourceClick(sourceUrl)
    }
  }

  return (
    <Box
      component="section"
      aria-labelledby="why-equal-parenting-title"
      className={className}
      data-testid={testId}
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: 'grey.50',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Title */}
        <Typography
          id="why-equal-parenting-title"
          variant="h2"
          component="h2"
          align="center"
          gutterBottom
          sx={{
            mb: 6,
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '2.5rem' },
          }}
        >
          {content.title}
        </Typography>

        {/* Key Points Grid */}
        <Grid container spacing={4}>
          {content.keyPoints.map(keyPoint => {
            const IconComponent = ICON_MAP[keyPoint.icon]

            return (
              <Grid item xs={12} md={6} key={keyPoint.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    {/* Icon */}
                    {IconComponent && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <IconComponent
                          aria-hidden="true"
                          sx={{
                            fontSize: 48,
                            color: 'primary.main',
                          }}
                        />
                      </Box>
                    )}

                    {/* Title */}
                    <Typography
                      variant="h3"
                      component="h3"
                      align="center"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        mb: 2,
                      }}
                    >
                      {keyPoint.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      paragraph
                      sx={{ mb: keyPoint.statistic ? 3 : 0 }}
                    >
                      {keyPoint.description}
                    </Typography>

                    {/* Statistic (if present) */}
                    {keyPoint.statistic && (
                      <Box
                        data-testid={`${keyPoint.id}-stat-value`}
                        sx={{
                          mt: 'auto',
                          pt: 2,
                          borderTop: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography
                          variant="h4"
                          component="div"
                          align="center"
                          sx={{
                            color: 'secondary.main',
                            fontWeight: 700,
                            fontSize: { xs: '2rem', md: '2.5rem' },
                            mb: 1,
                          }}
                        >
                          {keyPoint.statistic.value}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          align="center"
                          sx={{ mb: 1.5 }}
                        >
                          {keyPoint.statistic.label}
                        </Typography>

                        {/* Source Citation */}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          align="center"
                          component="div"
                        >
                          Source:{' '}
                          <Link
                            href={keyPoint.statistic.source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Source: ${keyPoint.statistic.source.name}`}
                            onClick={event =>
                              handleSourceClick(
                                event,
                                keyPoint.statistic!.source.url
                              )
                            }
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'underline',
                              '&:hover': {
                                color: 'primary.dark',
                              },
                            }}
                          >
                            {keyPoint.statistic.source.name}
                          </Link>
                          {' - '}
                          {keyPoint.statistic.source.citation}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      </Container>
    </Box>
  )
}

export default WhyEqualParentingSection
